import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ArrowRight, Globe, MapPin, Shield, Users, Package, DollarSign, Clock, CheckCircle, AlertTriangle, Search, Building, Store, ChefHat, Truck, Lock, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;
function fmtAmt(v) { return v >= 1000000 ? `$${(v / 1000000).toFixed(0)}M` : v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`; }
const roleIcons = { Exporter: Globe, Importer: Truck, Distributor: Building, "Ultimate Buyer": Store, "Importer (Proposed)": AlertTriangle };
const roleColors = { Exporter: "border-purple-400/50 bg-purple-400/5", Importer: "border-blue-400/50 bg-blue-400/5", Distributor: "border-cyan-400/50 bg-cyan-400/5", "Ultimate Buyer": "border-[#22C55E]/50 bg-[#22C55E]/5", "Importer (Proposed)": "border-yellow-400/50 bg-yellow-400/5" };
const roleTextColors = { Exporter: "text-purple-400", Importer: "text-blue-400", Distributor: "text-cyan-400", "Ultimate Buyer": "text-[#22C55E]", "Importer (Proposed)": "text-yellow-400" };

function ChainNode({ node }) {
  const Icon = roleIcons[node.role] || Globe;
  return (
    <div className={`border p-4 ${roleColors[node.role] || "border-[#1A2E22]"} min-w-[180px]`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${roleTextColors[node.role] || "text-white"}`} />
        <span className={`text-xs font-medium uppercase tracking-wider ${roleTextColors[node.role]}`}>{node.role}</span>
      </div>
      <p className="text-white text-sm font-semibold">{node.name}</p>
      <p className="text-[#A1B2A8]/50 text-xs mt-0.5">{node.country}{node.port ? ` — ${node.port}` : ""}{node.hub ? ` — ${node.hub}` : ""}</p>
      {node.confidence && <div className="flex items-center gap-1 mt-1.5"><Shield className={`w-3 h-3 ${node.confidence >= 80 ? "text-[#22C55E]" : node.confidence >= 50 ? "text-yellow-400" : "text-red-400"}`} /><span className="text-[#A1B2A8] text-[10px]">{node.confidence} · {node.credit_rating}</span></div>}
      {node.locations && <p className="text-[#A1B2A8]/40 text-[10px] mt-1">{node.locations.toLocaleString()} locations · {fmtAmt(node.annual_purchase)}/yr</p>}
      {node.flag && <p className="text-yellow-400 text-[10px] mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{node.flag}</p>}
    </div>
  );
}

function CashFlowComparison({ cashFlow }) {
  if (!cashFlow) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
      <div className="bg-red-400/5 border border-red-400/20 p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-red-400 text-xs font-medium uppercase tracking-wider">Traditional</span>
          <span className="text-red-400 font-['Outfit'] font-bold text-lg">{cashFlow.traditional.total_days} days</span>
        </div>
        {cashFlow.traditional.steps.map((s, i) => (
          <div key={i} className="flex items-center gap-2 mb-2">
            <div className="w-1 h-8 bg-red-400/30" />
            <div><p className="text-[#A1B2A8] text-xs">{s.from} → {s.to}</p><p className="text-[#A1B2A8]/40 text-[10px]">{s.days}d — {s.label}</p></div>
          </div>
        ))}
      </div>
      <div className="bg-[#22C55E]/5 border border-[#22C55E]/20 p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[#22C55E] text-xs font-medium uppercase tracking-wider">Delta GreenX</span>
          <span className="text-[#22C55E] font-['Outfit'] font-bold text-lg">{cashFlow.delta_greenx.total_days} days</span>
        </div>
        {cashFlow.delta_greenx.steps.map((s, i) => (
          <div key={i} className="flex items-center gap-2 mb-2">
            <div className="w-1 h-8 bg-[#22C55E]/30" />
            <div><p className="text-[#A1B2A8] text-xs">{s.from} → {s.to}</p><p className="text-[#A1B2A8]/40 text-[10px]">{s.days === 0 ? "Instant" : `${s.days}d`} — {s.label}</p></div>
          </div>
        ))}
        <div className="mt-3 pt-3 border-t border-[#22C55E]/20 flex items-center justify-between">
          <span className="text-[#22C55E] text-xs">{cashFlow.savings_days} days saved</span>
          <span className="text-[#22C55E] font-['Outfit'] font-semibold text-sm">{cashFlow.capital_acceleration}</span>
        </div>
      </div>
    </div>
  );
}

export default function AdminSupplyChain() {
  const [chains, setChains] = useState([]);
  const [reach, setReach] = useState({});
  const [dnbQuery, setDnbQuery] = useState("");
  const [dnbResults, setDnbResults] = useState([]);
  const [dnbLoading, setDnbLoading] = useState(false);
  const [selectedChain, setSelectedChain] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([
        axios.get(`${API}/admin/supply-chains`, { withCredentials: true }),
        axios.get(`${API}/admin/platform-reach`, { withCredentials: true }),
      ]);
      setChains(c.data); setReach(r.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const searchDnb = async (q) => {
    setDnbQuery(q);
    if (q.length < 2) { setDnbResults([]); return; }
    setDnbLoading(true);
    try { const { data } = await axios.get(`${API}/admin/dnb-lookup?q=${encodeURIComponent(q)}`, { withCredentials: true }); setDnbResults(data); }
    catch (e) { console.error(e); }
    finally { setDnbLoading(false); }
  };

  return (
    <div data-testid="admin-supply-chain">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-white">Supply Chain Mapping</h1>
          <p className="text-[#A1B2A8]/60 text-sm">Exporter → Importer → Distributor → Ultimate Buyer · Cash Flow Cycles</p>
        </div>
      </div>

      {/* Platform Reach KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        {[
          [Package, "Active Chains", reach.total_chains, "text-white"],
          [Globe, "Corridors", reach.active_corridors, "text-[#22C55E]"],
          [Store, "Ultimate Buyers", reach.total_ultimate_buyers, "text-blue-400"],
          [MapPin, "Buyer Locations", reach.ultimate_buyer_locations?.toLocaleString(), "text-purple-400"],
          [DollarSign, "Downstream", fmtAmt(reach.total_downstream_annual || 0), "text-[#22C55E]"],
          [RotateCcw, "Cycle Reduction", `${reach.cycle_reduction || 0}%`, "text-[#22C55E]"],
        ].map(([Icon, label, value, color]) => (
          <div key={label} className="bg-[#09120D] border border-[#1A2E22] p-4">
            <div className="flex items-center gap-2 mb-1"><Icon className={`w-3.5 h-3.5 ${color}`} /><span className="text-[#A1B2A8] text-[10px] uppercase">{label}</span></div>
            <span className={`font-['Outfit'] text-xl font-bold ${color}`}>{value}</span>
          </div>
        ))}
      </div>

      {/* Commercial Lookup */}
      <div className="bg-[#09120D] border border-[#1A2E22] p-5 mb-6">
        <h2 className="font-['Outfit'] text-sm font-semibold text-white mb-3 uppercase tracking-wider flex items-center gap-2"><Search className="w-4 h-4 text-[#22C55E]" /> Multi-Source Corporate Intelligence <span className="text-[#A1B2A8] text-[10px] tracking-[0.2em] font-normal normal-case">· IN · US · UK · SG · AU</span></h2>
        <p className="text-[#A1B2A8]/60 text-[10px] mb-3 normal-case tracking-normal">Aggregated public-registry signals — orientation only; full KYC pending formal review and counterparty consent.</p>
        <div className="relative">
          <Input value={dnbQuery} onChange={(e) => searchDnb(e.target.value)} placeholder="Search company name (e.g., Nordic, Pacific, Mumbai...)"
            className="bg-[#050A07] border-[#1A2E22] text-white placeholder:text-[#A1B2A8]/30 focus:border-[#22C55E]" data-testid="dnb-search" />
          {dnbLoading && <div className="absolute right-3 top-3"><div className="w-4 h-4 border-2 border-[#22C55E] border-t-transparent rounded-full animate-spin" /></div>}
        </div>
        {dnbResults.length > 0 && (
          <div className="mt-3 space-y-2">
            {dnbResults.map((r) => (
              <div key={r.id} className="bg-[#050A07] border border-[#1A2E22] p-4 flex items-center justify-between" data-testid={`dnb-result-${r.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className={`font-['Outfit'] text-2xl font-bold ${r.credit_rating === "NR" ? "text-[#A1B2A8]/40" : r.credit_rating.startsWith("A") ? "text-[#22C55E]" : r.credit_rating.startsWith("B") ? "text-yellow-400" : "text-red-400"}`}>{r.credit_rating}</span>
                    <div>
                      <p className="text-white text-sm font-semibold">{r.entity_name}</p>
                      <p className="text-[#A1B2A8]/50 text-xs">{r.legal_name} · {r.country} · {r.registration_label || "Registry ID"}: {r.registration_number || r.registration_id || r.duns_number}</p>
                      {r.data_source && <p className="text-[#22C55E]/60 text-[10px] mt-0.5">Multi-source corporate intelligence</p>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-right">
                  <div><p className="text-[#A1B2A8] text-[10px]">Strength</p><p className="text-white font-semibold">{r.financial_strength}/10</p></div>
                  <div><p className="text-[#A1B2A8] text-[10px]">Revenue</p><p className="text-white font-semibold">{r.annual_revenue}</p></div>
                  <div><p className="text-[#A1B2A8] text-[10px]">Risk</p><p className={`font-semibold ${r.risk_indicator === "Low" ? "text-[#22C55E]" : r.risk_indicator === "Moderate" ? "text-yellow-400" : "text-red-400"}`}>{r.risk_indicator}</p></div>
                  <span className={`text-xs px-2 py-0.5 border capitalize ${r.kyc_status === "complete" ? "text-[#22C55E] border-[#22C55E]/30" : r.kyc_status === "rejected" ? "text-red-400 border-red-400/30" : "text-yellow-400 border-yellow-400/30"}`}>KYC: {r.kyc_status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Chain Selector */}
      <div className="flex flex-wrap gap-2 mb-4">
        {chains.map((c) => (
          <button key={c.id} onClick={() => setSelectedChain(selectedChain === c.id ? null : c.id)}
            className={`text-xs px-4 py-2 border transition-colors ${selectedChain === c.id ? "border-[#22C55E]/50 bg-[#22C55E]/10 text-[#22C55E]" : c.status === "active" ? "border-[#1A2E22] text-[#A1B2A8] hover:text-white" : "border-yellow-400/30 text-yellow-400"}`}
            data-testid={`chain-${c.id}`}>
            {c.corridor} {c.status !== "active" && "⚠"}
          </button>
        ))}
      </div>

      {/* Chain Details */}
      {chains.filter(c => !selectedChain || c.id === selectedChain).map((chain) => (
        <div key={chain.id} className="bg-[#09120D] border border-[#1A2E22] p-6 mb-4" data-testid={`chain-detail-${chain.id}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-['Outfit'] text-lg font-bold text-white">{chain.corridor}</h3>
              <p className="text-[#A1B2A8]/50 text-xs">{chain.products.join(", ")} · {chain.annual_volume > 0 ? fmtAmt(chain.annual_volume) + "/yr" : "No volume yet"}</p>
            </div>
            <span className={`text-xs px-2 py-0.5 border ${chain.status === "active" ? "text-[#22C55E] border-[#22C55E]/30 bg-[#22C55E]/10" : "text-yellow-400 border-yellow-400/30 bg-yellow-400/10"}`}>{chain.status.replace("_", " ")}</span>
          </div>

          {/* Visual Chain */}
          <div className="flex items-start gap-0 overflow-x-auto pb-2">
            {(() => {
              const grouped = {};
              chain.nodes.forEach(n => {
                if (!grouped[n.position]) grouped[n.position] = [];
                grouped[n.position].push(n);
              });
              const positions = Object.keys(grouped).sort((a, b) => a - b);
              return positions.map((pos, idx) => (
                <div key={pos} className="flex items-center">
                  <div className="flex flex-col gap-2">
                    {grouped[pos].map((node, ni) => (
                      <ChainNode key={`${pos}-${ni}`} node={node} />
                    ))}
                  </div>
                  {idx < positions.length - 1 && (
                    <div className="flex items-center px-2 flex-shrink-0">
                      <ArrowRight className="w-5 h-5 text-[#22C55E]/40" />
                    </div>
                  )}
                </div>
              ));
            })()}
          </div>

          {/* Cash Flow Comparison */}
          {chain.cash_flow && <CashFlowComparison cashFlow={chain.cash_flow} />}

          {chain.status === "pending_verification" && (
            <div className="mt-4 bg-yellow-400/5 border border-yellow-400/20 p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-yellow-400 font-semibold text-sm">Chain Incomplete — Exporter Verification Required</p>
                <p className="text-[#A1B2A8] text-xs mt-1">Mumbai Premium Foods has not completed KYC. Counterparty registry verification pending. No trades can execute on this chain until verification is complete.</p>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Investor Insight */}
      <div className="bg-[#09120D] border border-[#22C55E]/20 p-6 mt-6">
        <div className="flex items-start gap-3">
          <DollarSign className="w-5 h-5 text-[#22C55E] mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-[#22C55E] font-['Outfit'] font-semibold text-sm">Investor Insight: End-to-End Visibility</p>
            <p className="text-[#A1B2A8] text-sm mt-1 leading-relaxed">
              Delta GreenX maps the entire chain from exporter to ultimate buyer — covering {reach.total_ultimate_buyers || 0} downstream buyers across {(reach.ultimate_buyer_locations || 0).toLocaleString()} retail/restaurant locations representing ${((reach.total_downstream_annual || 0) / 1000000).toFixed(1)}M in annual downstream purchasing power.
              Traditional settlement takes {reach.traditional_avg_cycle || 0} days. Our infrastructure cuts this to {reach.greenx_avg_cycle || 0} days — a {reach.cycle_reduction || 0}% acceleration that unlocks working capital across the entire chain.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
